/*
 * Copyright (c) 2026. Bluemate B.V.
 */
import {Component, ErrorInfo, ReactNode} from 'react';

interface Props {
    // Changing this (e.g. the route pathname) clears a previous error so the boundary can retry.
    resetKey: string;
    children: ReactNode;
}

interface State {
    hasError: boolean;
}

/**
 * Isolates the "Subscription is paused" paywall from the rest of the app. The paywall dialog renders
 * a plain DOM node; if a user deletes that node from the DOM by hand (to try to bypass the paywall),
 * React later throws `removeChild ... not a child of this node` when it unmounts the dialog (e.g. on
 * navigating to a paywall-exempt settings page). Without a boundary that error bubbles to React
 * Router and blanks the whole route. Here we swallow it and render nothing, then reset on the next
 * navigation so the paywall works again. The backend gate still blocks the data regardless.
 */
export default class PaywallErrorBoundary extends Component<Props, State> {
    state: State = {hasError: false};

    static getDerivedStateFromError(): State {
        return {hasError: true};
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.warn('Paywall render error swallowed (likely manual DOM removal):', error, info.componentStack);
    }

    componentDidUpdate(prevProps: Props) {
        if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
            this.setState({hasError: false});
        }
    }

    render() {
        return this.state.hasError ? null : this.props.children;
    }
}
